import { useAuth } from "@west-tennessee-consulting/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import type { Message } from "@/lib/core/crm/types";
import { performMessageAction } from "./api-client/index";
import type { MessageActionVariables } from "./types";
import { ACTION_SUCCESS_MESSAGES, STATE_MODIFYING_ACTIONS } from "./constants";
import {
  parseApiError,
  shouldRetry,
  getErrorMessage,
  getErrorRecoveryAction,
  getRetryDelay,
  requiresReauth,
} from "./error-handler";
import type { MessageApiError } from "./error-types";

interface MessageActionContext {
  previousMessages?: unknown;
  previousMessage?: Message;
}

/**
 * Hook for performing actions on messages (mark as read, delete, archive, etc.)
 * Includes optimistic updates, rollback on error, and retry logic for transient failures
 */
export function useMessageAction() {
  const queryClient = useQueryClient();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  return useMutation<unknown, Error, MessageActionVariables, MessageActionContext>({
    mutationFn: (variables: MessageActionVariables) =>
      performMessageAction(variables, token || undefined),
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey: ["messages"] });
      await queryClient.cancelQueries({
        queryKey: [
          "provider-message",
          variables.provider,
          variables.messageId,
          variables.connectionId,
        ],
      });

      // Snapshot the previous value
      const previousMessages = queryClient.getQueryData(["messages"]);
      const previousMessage = queryClient.getQueryData<Message | undefined>([
        "provider-message",
        variables.provider,
        variables.messageId,
        variables.connectionId,
      ]);

      // Optimistically update the message based on action
      if (STATE_MODIFYING_ACTIONS.includes(variables.action)) {
        // Update message detail query
        queryClient.setQueryData(
          [
            "provider-message",
            variables.provider,
            variables.messageId,
            variables.connectionId,
          ],
          (old: Message | undefined) => {
            if (!old) return old;
            return {
              ...old,
              isRead:
                variables.action === "mark_as_read"
                  ? true
                  : variables.action === "mark_as_unread"
                    ? false
                    : old.isRead,
              isArchived: variables.action === "archive" ? true : old.isArchived,
              isDeleted: variables.action === "delete" ? true : old.isDeleted,
            };
          }
        );

        // Update message lists query
        queryClient.setQueryData(["messages"], (old: unknown) => {
          if (!old || typeof old !== "object" || !("messages" in old)) return old;
          const oldData = old as { messages: Message[] };
          return {
            ...oldData,
            messages: oldData.messages.map((msg: Message) => {
              if (msg.id === variables.messageId) {
                return {
                  ...msg,
                  isRead:
                    variables.action === "mark_as_read"
                      ? true
                      : variables.action === "mark_as_unread"
                        ? false
                        : msg.isRead,
                  isArchived: variables.action === "archive" ? true : msg.isArchived,
                  isDeleted: variables.action === "delete" ? true : msg.isDeleted,
                };
              }
              return msg;
            }),
          };
        });
      }

      return { previousMessages, previousMessage };
    },
    onSuccess: (data, variables) => {
      // Show success toast with action-specific message
      toast.success(ACTION_SUCCESS_MESSAGES[variables.action] || "Action completed successfully");

      // Batch invalidate queries instead of multiple calls
      void queryClient.invalidateQueries({
        queryKey: ["messages"],
      });

      // Invalidate the specific message detail query for actions that change message state
      if (STATE_MODIFYING_ACTIONS.includes(variables.action)) {
        void queryClient.invalidateQueries({
          queryKey: [
            "provider-message",
            variables.provider,
            variables.messageId,
            variables.connectionId,
          ],
        });
      }
    },
    onError: (error: unknown, variables, context) => {
      // Parse the error into a structured type
      const apiError = error instanceof Error ? parseApiError(error) : parseApiError(error);

      // Rollback to previous state on error
      if (context) {
        if (context.previousMessages) {
          queryClient.setQueryData(["messages"], context.previousMessages);
        }
        if (context.previousMessage) {
          queryClient.setQueryData(
            [
              "provider-message",
              variables.provider,
              variables.messageId,
              variables.connectionId,
            ],
            context.previousMessage
          );
        }
      }

      // Get user-friendly error message
      const errorMessage = getErrorMessage(apiError);

      // Show error toast with recovery action hint
      const recoveryAction = getErrorRecoveryAction(apiError);
      const toastMessage =
        recoveryAction.type === "retry"
          ? `${errorMessage} ${recoveryAction.description}`
          : errorMessage;

      toast.error(toastMessage);

      // Log error for debugging
      if (process.env.NODE_ENV === "development") {
        logger.error("❌ Message action failed", apiError, {
          variables,
          recoveryAction,
        });
      }

      // Handle auth errors specially
      if (requiresReauth(apiError)) {
        // Trigger re-authentication flow
        // This could dispatch an action to your auth context or redirect to login
        logger.warn("⚠️ Re-authentication required");
      }
    },
    retry: (failureCount, error) => {
      // Parse error and check if it should be retried
      const apiError = error instanceof Error ? parseApiError(error) : parseApiError(error);

      // Don't retry more than 3 times
      if (failureCount >= 3) {
        return false;
      }

      // Only retry if the error indicates it's retryable
      return shouldRetry(apiError);
    },
    retryDelay: (attemptIndex, error) => {
      // Parse error and get retry delay
      const apiError = error instanceof Error ? parseApiError(error) : parseApiError(error);
      return getRetryDelay(apiError);
    },
  });
}
